import * as d3 from 'd3';

import { appendSelect } from 'd3-appendselect';
import merge from 'lodash/merge';

d3.selection.prototype.appendSelect = appendSelect;

class Chart {
  selection(selector) {
    if (!selector) return this._selection;
    this._selection = d3.select(selector);
    return this;
  }

  data(newData) {
    if (!newData) return this._data || this.defaultData;
    this._data = newData;
    return this;
  }

  props(newProps) {
    if (!newProps) return this._props || this.defaultProps;
    this._props = merge(this._props || this.defaultProps, newProps);
    return this;
  }

  defaultData = []

  defaultProps = {
    height: 225,
    margin: {
      top: 35,
      right: 40,
      bottom: 22,
      left: 40,
    },
  };

  draw() {
    const data = this.data();
    const props = this.props();
    const container = this.selection().node();
    const { width: containerWidth } = container.getBoundingClientRect();
    const width = containerWidth - props.margin.left - props.margin.right;
    const height = props.height - props.margin.top - props.margin.bottom;

    const { vaccinations, country } = data;

    const peopleVaccinated = vaccinations.peopleVaccinated.map(d => ({
      date: new Date(d.date),
      percent: d.count / country.dataProfile.population.d,
      type: 'one dose',
    }));

    const peopleFullyVaccinated = vaccinations.peopleFullyVaccinated.map(d => ({
      date: new Date(d.date),
      percent: d.count / country.dataProfile.population.d,
      type: 'fully vaccinated',
    }));

    const latestY = peopleVaccinated.slice(-1)[0].percent;
    const dataYMax = d3.max(peopleVaccinated, d => d.percent);
    const yMax = [0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 1].find(d => d > dataYMax);

    const dateExtent = d3.extent(peopleVaccinated, d => d.date);

    const xScale = d3.scaleLinear()
      .domain(dateExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, yMax || 1])
      .range([height, 0]);

    const xAxis = d3.axisBottom(xScale)
      .tickValues(xScale.domain())
      .tickPadding(5)
      .tickFormat(d3.timeFormat('%b. %e, %Y'));

    const tickvalues = (yMax / 2 * 100) % 5 === 0 ?
      [0, yMax / 2, yMax] : [0, yMax];
    const yAxis = d3.axisLeft(yScale)
      .tickFormat(d3.format('.0%'))
      .tickValues(tickvalues);

    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.percent));

    const plot = this.selection()
      .appendSelect('svg')
      .attr('width', containerWidth)
      .attr('height', props.height)
      .appendSelect('g.plot')
      .attr('transform', `translate(${props.margin.left},${props.margin.top})`);

    plot.appendSelect('g.axis.x')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    plot.appendSelect('g.axis.y')
      .call(yAxis);

    plot.appendSelect('path.vaccinated')
      .datum(peopleVaccinated)
      .style('fill', 'none')
      .style('stroke', 'rgba(163, 190, 140, 0.5)')
      .style('stroke-width', '2px')
      .attr('d', line);

    plot.appendSelect('path.fully-vaccinated')
      .datum(peopleFullyVaccinated)
      .style('fill', 'none')
      .style('stroke', '#A3BE8C')
      .style('stroke-width', '2.5px')
      .attr('d', line);
    
    plot.appendSelect('text.data-label.vaccinated-label')
      .attr('x', width + 3)
      .attr('y', yScale(latestY) + 5)
      .style('fill', 'rgba(163, 190, 140, 0.5)')
      .text(`${Math.round(latestY * 1000) / 10}%`);

    const key = this.selection()
      .select('svg')
      .appendSelect('g.key');

    key.appendSelect('line.vaccinated')
      .attr('x1', 0)
      .attr('x2', 15)
      .attr('y1', 7)
      .attr('y2', 7)
      .style('stroke', 'rgba(163, 190, 140, 0.5)')
      .style('stroke-width', 2);
    key.appendSelect('text.vaccinated')
      .attr('x', 20)
      .attr('y', 12)
      .style('fill', 'rgba(163, 190, 140, 0.7)')
      .text('At least 1 dose');

    const offset = 125;

    key.appendSelect('line.fully-vaccinated')
      .attr('x1', 0 + offset)
      .attr('x2', 15 + offset)
      .attr('y1', 7)
      .attr('y2', 7)
      .style('stroke', '#A3BE8C')
      .style('stroke-width', 2.5);
    key.appendSelect('text.fully-vaccinated')
      .attr('x', 20 + offset)
      .attr('y', 12)
      .style('fill', '#A3BE8C')
      .text('Fully vaccinated');

    return this; // Generally, always return the chart class from draw!
  }
}

export default Chart;
